import type { EmailType } from "hooks/api/use-email-types";
import type { SavedTheme } from "hooks/api/use-theme";
import type {
  APIFormatBaseTypographyInput,
  APIFormatThemeButtonData,
  APIFormatThemeLayoutData,
  APILayoutResponsivePadding,
  APIPaddingOptions,
  APIParagraphTypographyInput,
  BeeFontData,
  ColorsFormData,
} from "containers/admin/clients/client/theme-page/preview/preview.types";

import { BASE_FONT_STACK } from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.constants";

type Declarations = Record<string, string | undefined>;

type StyleContext = {
  colors?: Partial<ColorsFormData>;
  beeFonts: BeeFontData[];
  isBaseFontApplied: boolean;
};

const BUTTON_ALIGNMENT: Record<string, string> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

/** The legacy theme stores sizes either as bare numbers or with their unit. */
const toPx = (value: string | number | undefined) =>
  value === undefined || value === "" ? undefined : /^\d+(\.\d+)?$/.test(String(value)) ? `${value}px` : String(value);

/**
 * Theme values are written into the preview's `<style>`, where `<` could close it and `{ } ;` could open new
 * rules, so those characters are dropped — no legitimate colour, size or font stack contains them.
 */
const toCssValue = (value: string) => value.replace(/[<>{};\\]/g, "");

const toRule = (selector: string, declarations: Declarations) => {
  const body = Object.entries(declarations)
    .filter((entry): entry is [string, string] => !!entry[1])
    .map(([property, value]) => `${property}: ${toCssValue(value)};`)
    .join(" ");

  return body ? `${selector} { ${body} }` : "";
};

const toPadding = (padding?: APIPaddingOptions) =>
  padding
    ? [padding.top, padding.right, padding.bottom, padding.left].map((side) => toPx(side) ?? "0px").join(" ")
    : undefined;

/** Theme colours are stored either as a palette key (`intense`) or as a hex value. */
const resolveColor = (value: string | undefined, colors: StyleContext["colors"]) => {
  if (!value) {
    return undefined;
  }

  if (value === "none") {
    return "transparent";
  }

  return value.startsWith("#") ? value : colors?.[value] || undefined;
};

/**
 * Same resolution as the legacy preview: the section's font comes from its BEE font, with the section's
 * fallback appended; "Base font style" shows the fallback alone.
 */
const resolveFontFamily = (
  { fontFamily, fallbackFontFamily }: APIFormatBaseTypographyInput,
  { beeFonts, isBaseFontApplied }: StyleContext,
) => {
  if (isBaseFontApplied) {
    return fallbackFontFamily || BASE_FONT_STACK;
  }

  const beeFont = beeFonts.find((font) => font.value === fontFamily);

  if (!beeFont) {
    return undefined;
  }

  return fallbackFontFamily ? `${beeFont.fontFamily.split(",")[0]}, ${fallbackFontFamily}` : beeFont.fontFamily;
};

const typographyDeclarations = (
  typography: (APIFormatBaseTypographyInput & { alignment?: string }) | undefined,
  context: StyleContext,
): Declarations =>
  typography
    ? {
        "font-family": resolveFontFamily(typography, context),
        "font-size": toPx(typography.fontSize),
        "line-height": typography.lineHeight,
        "font-weight": typography.fontWeight,
        color: resolveColor(typography.textColor, context.colors),
        "text-align": typography.alignment,
      }
    : {};

const buttonDeclarations = (button: APIFormatThemeButtonData | undefined, context: StyleContext): Declarations => {
  if (!button) {
    return {};
  }

  const { border } = button;

  return {
    "background-color": resolveColor(button.backgroundColor, context.colors),
    padding: toPadding(button.padding),
    width: button.width && button.width !== "auto" ? `${button.width}%` : undefined,
    "justify-content": "center",
    "border-radius": toPx(border?.radius),
    border:
      border && border.style !== "none"
        ? `${toPx(border.width) ?? "1px"} ${border.style} ${resolveColor(border.color, context.colors) ?? "currentColor"}`
        : undefined,
  };
};

/** Small previews the mobile paddings, Medium and Large the desktop ones — the legacy Mobile/Desktop toggle. */
const responsivePaddingRules = (selector: string, padding: APILayoutResponsivePadding | undefined) =>
  padding
    ? [
        toRule(selector, { padding: toPadding(padding.mobile) }),
        `@media (min-width: 640px) { ${toRule(selector, { padding: toPadding(padding.desktop) })} }`,
      ].join("\n")
    : "";

const layoutRules = (layout: APIFormatThemeLayoutData | undefined) => {
  if (!layout) {
    return "";
  }

  const { contentArea, contentBlock } = layout;
  const blockPadding = (block: "headings" | "paragraph" | "button") =>
    contentBlock?.group === "all-blocks" ? contentBlock.allBlocks : contentBlock?.[block];

  return [
    toRule(".email-body", { padding: toPadding(contentArea), gap: "0px" }),
    responsivePaddingRules(".email-headline h1, .email-headline h2", blockPadding("headings")),
    responsivePaddingRules(".email-text, .email-signature p", blockPadding("paragraph")),
    responsivePaddingRules(".email-body .button-row", blockPadding("button")),
  ].join("\n");
};

/** CSS that applies the saved theme to the sample email. Sections the theme does not set keep the template's defaults. */
export const getEmailThemeStyles = (theme: SavedTheme | undefined, isBaseFontApplied: boolean) => {
  const context: StyleContext = {
    colors: theme?.colors,
    beeFonts: theme?.fonts?.beeFonts ?? [],
    isBaseFontApplied,
  };
  const typography = theme?.typography;
  const paragraph: APIParagraphTypographyInput | undefined = typography?.paragraph;

  return [
    toRule(".email-headline h1", typographyDeclarations(typography?.heading, context)),
    toRule(".email-headline h2", typographyDeclarations(typography?.subHeading, context)),
    toRule(".email-text, .email-signature", typographyDeclarations(paragraph, context)),
    toRule(".email-text .link", { color: resolveColor(paragraph?.linkColor, context.colors) }),
    toRule(".email-body .button", {
      ...typographyDeclarations(typography?.buttonLabel, context),
      ...buttonDeclarations(theme?.button, context),
    }),
    toRule(".email-body .button-row", {
      "justify-content": theme?.button && BUTTON_ALIGNMENT[theme.button.alignment],
    }),
    layoutRules(theme?.layout),
  ]
    .filter(Boolean)
    .join("\n");
};

/** Every web font the theme uses, so the isolated preview document can load them itself. */
export const getThemeFontStylesheets = (theme: SavedTheme | undefined) =>
  Array.from(new Set((theme?.fonts?.beeFonts ?? []).map(({ url }) => url).filter(Boolean)));

const HTML_ESCAPES: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

/** Theme values written into the preview's HTML (not its CSS — see `toCssValue`) are escaped first. */
export const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);

export type PreviewSender = { name: string; email: string };

const NOT_DEFINED = "Not defined";

/** The legacy preview's header: the default sender of the client's default email type. */
export const getDefaultSender = (emailTypes: EmailType[] = []): PreviewSender => {
  const sender = emailTypes
    .find(({ defaultForClient }) => defaultForClient)
    ?.senders?.find(({ defaultForEmailType }) => defaultForEmailType);

  return {
    name: sender?.senderName?.trim() || NOT_DEFINED,
    email: sender?.senderEmail?.trim() || NOT_DEFINED,
  };
};
